import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, ExternalLink, Download, CalendarClock, TriangleAlert, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  renewal_pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  past_due: "bg-red-500/15 text-red-400 border-red-500/30",
  expired: "bg-red-500/15 text-red-400 border-red-500/30",
  none: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};
const STATUS_LABEL = { active: "Active", renewal_pending: "Renewal pending", past_due: "Past due", expired: "Expired", none: "No subscription" };
const INV_STATUS = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  open: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  void: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  uncollectible: "bg-red-500/15 text-red-400 border-red-500/30",
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function AdvertiserBilling() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/partner/billing/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/partner/receipts").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e.response?.data?.detail)); setRows([]); });
  }, []);

  if (!summary || rows === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const updateCard = async () => {
    try { const { data } = await api.post("/partner/billing/portal"); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const badge = STATUS_BADGE[summary.status] || STATUS_BADGE.none;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="advertiser-billing">
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="billing-summary">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-primary mb-1">Advertiser billing</div>
            <h2 className="font-heading text-xl font-bold">{summary.advertiserName || "Your business"}</h2>
            <p className="text-sm text-muted-foreground mt-1">{summary.planLabel || "Annual"} fee ${Number(summary.planFee ?? summary.annualFee).toFixed(2)} · Support <a className="text-primary" href="mailto:support@velopaths.com">support@velopaths.com</a></p>
          </div>
          <Badge className={`${badge} border`} data-testid="billing-status-badge">{STATUS_LABEL[summary.status] || summary.status}</Badge>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
          <div><div className="text-muted-foreground">Coverage period</div><div className="font-medium" data-testid="billing-coverage">{summary.coverageStart ? `${summary.coverageStart} → ${summary.coverageEnd}` : "—"}</div></div>
          <div><div className="text-muted-foreground">Next renewal</div><div className="font-medium">{summary.nextRenewalDate || "—"}</div></div>
        </div>
        <Button size="sm" variant="outline" className="mt-4" onClick={updateCard} data-testid="update-card-btn"><CreditCard className="w-4 h-4 mr-1" />Manage billing & payment method</Button>
      </div>

      {summary.status === "renewal_pending" && summary.nextRenewalDate && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300" data-testid="renewal-callout">
          <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />Your advertiser program renews on {summary.nextRenewalDate}. Stripe will charge your card automatically.
        </div>
      )}
      {summary.status === "past_due" && (
        <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="pastdue-callout">
          <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />Your renewal payment failed. Please update your card or contact Velopaths support to restore coverage.
        </div>
      )}

      <div>
        <h3 className="font-heading font-bold mb-2">Invoices</h3>
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="billing-empty">No invoices yet. Your first invoice appears here once your subscription is active.</div>
        ) : (
          <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3" data-testid={`billing-row-${r.id}`}>
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {r.number || r.id}
                    <Badge className={`${INV_STATUS[r.status] || INV_STATUS.draft} border text-[10px]`}>{r.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">${Number(r.amount).toFixed(2)} {r.currency} · {r.date ? new Date(r.date).toLocaleDateString() : ""}{r.periodStart ? ` · ${r.periodStart}→${r.periodEnd}` : ""}</div>
                </div>
                {r.hostedUrl && <Button size="sm" variant="ghost" onClick={() => window.open(r.hostedUrl, "_blank")} data-testid={`billing-view-${r.id}`}><ExternalLink className="w-4 h-4 mr-1" />View</Button>}
                {r.pdfUrl && <Button size="sm" variant="outline" onClick={() => window.open(r.pdfUrl, "_blank")} data-testid={`billing-download-${r.id}`}><Download className="w-4 h-4 mr-1" />PDF</Button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
